import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { v4 as uuid } from 'uuid';
import {
  EmptyState,
  FieldType,
  FilterBuilder,
  Loading
} from 'onefaceui-react-components';

import { getResourceTo, formatDateISO } from 'react/utils/utils';

import { usePhotoAlbumByUser } from 'react/queries/PhotoAlbumGallery';
import { getLookupExtraParams } from 'react/utils/lookupUtils';
import { actionCreators as actionsPhotoAlbum } from 'react/api/actions/photoAlbum';
import {
  Container,
  FilterBuilderContainer,
  FlexContainer,
  FlexContainerLoading,
  GridContainer,
  GridContent
} from './PhotoAlbumListStyles';
import { PhotoAlbumItem } from './PhotoAlbumItem';
import {
  getLocation,
  getTimeInputFormat
} from 'react/utils/onefaceLibraryUtils';
import PhotoAlbumSelectionBar from 'react/containers/modals/PhotoAlbumModal/PhotoAlbumSelectionBar';
import { downloadSelectedPhotos } from 'react/containers/modals/PhotoAlbumModal/utils';
import SelectionLoadingOverlay from './SelectionLoadingOverlay';

export function PhotoAlbumList() {
  const dispatch = useDispatch();
  const { selectedAlbums } = useSelector((state) => state.photoAlbum).toJS();
  const [filters, setFilters] = useState({
    maxResult: 51,
    firstResult: 0,
    users: []
  });
  const [isLoadingDownloadButton, setIsLoadingDownloadButton] = useState(false);
  const filterBuilderRef = useRef();

  const { data, isFetching, fetchNextPage, status, isFetchingNextPage } =
    usePhotoAlbumByUser(filters);

  const userFormat = useMemo(() => {
    return {
      timeFormat: getTimeInputFormat(
        Monet.Session.User.preferedLocale.timeFormat
      ),
      location: getLocation(Monet.Session.User.preferedLocale.language)
    };
  }, []);

  const filterBuilderFields = [
    {
      id: 'dateStart',
      label: getResourceTo('PhotoAlbum.filter.dateStart'),
      name: 'dateStart',
      type: FieldType.DATETIME,
      extra: {
        dataTestId: 'start-date-field',
        role: 'presentation',
        'data-format': userFormat.timeFormat,
        autoComplete: 'off',
        locale: userFormat.location,
        timeFormat: userFormat.timeFormat
      }
    },
    {
      id: 'dateEnd',
      label: getResourceTo('PhotoAlbum.filter.dateEnd'),
      name: 'dateEnd',
      type: FieldType.DATETIME,
      extra: {
        dataTestId: 'end-date-field',
        role: 'presentation',
        'data-format': userFormat.timeFormat,
        autoComplete: 'off',
        locale: userFormat.location,
        timeFormat: userFormat.timeFormat
      }
    },
    {
      id: 'driverIDs',
      label: getResourceTo('PhotoAlbum.filter.driver'),
      name: 'driverIDs',
      type: FieldType.LOOKUP_MULTISELECT,
      defaultField: 'name',
      defaultAttribute: 'name',
      extra: getLookupExtraParams({
        entity: 'Driver',
        defaultAttribute: 'name',
        dataTestId: 'driver-field',
        autoComplete: 'off'
      })
    }
  ];

  const { pages } = data || {};

  const hasData = useMemo(() => {
    return status === 'success' && pages && pages[0]?.length > 0;
  }, [status, pages]);

  const hasNoData = status === 'success' && pages && pages[0]?.length === 0;
  const isLoading = status === 'pending' && isFetching && !isFetchingNextPage;
  const isLoadingMoreAlbums = status === 'loading' || isFetchingNextPage;
  const isSelectedAll = useMemo(() => {
    if (!pages || pages.length === 0) return false;

    const pageAlbumsIDs = pages
      .flatMap((group) => group)
      .filter(({ photos }) => photos.length > 0)
      .map(({ id }) => id);

    if (pageAlbumsIDs.length === 0) return false;

    const selectedAlbumsIDs = Object.entries(selectedAlbums)
      .filter(([, isSelected]) => Boolean(isSelected))
      .map(([id]) => Number(id));

    return (
      selectedAlbumsIDs.length === pageAlbumsIDs.length &&
      pageAlbumsIDs.every((id) => selectedAlbumsIDs.includes(id))
    );
  }, [selectedAlbums, pages]);

  function isInvalidEndDate(dateStart, dateEnd) {
    if (!dateStart && !dateEnd) return false;
    return new Date(dateEnd).getTime() < new Date(dateStart).getTime();
  }

  const handleFilterSubmit = useCallback((fieldsValues) => {
    const { dateStart, dateEnd, driverIDs } = fieldsValues;

    if (isInvalidEndDate(dateStart, dateEnd)) {
      filterBuilderRef.current?.setErrors({
        dateEnd: getResourceTo('Gallery.filter.dateError')
      });
    }

    if (!isInvalidEndDate(dateStart, dateEnd)) {
      filterBuilderRef.current?.clearErrors();
      setFilters(() => ({
        start: dateStart ? formatDateISO(new Date(dateStart)) : undefined,
        end: dateEnd ? formatDateISO(new Date(dateEnd)) : undefined,
        users: driverIDs ? driverIDs.map((driver) => ({ id: driver.id })) : [],
        maxResult: 15,
        firstResult: 0
      }));
    }
  }, []);

  const handleChangeSelectAll = (isChecked) => {
    if (isChecked) {
      const allAlbumsIds = pages
        .flatMap((item) => item)
        .filter(({ photos }) => photos.length > 0)
        .map(({ id }) => id);

      dispatch(actionsPhotoAlbum.selectMultipleAlbums(allAlbumsIds));
      return;
    }

    dispatch(actionsPhotoAlbum.clear());
  };

  const handleClickDownloadPhotos = () => {
    downloadSelectedPhotos(selectedAlbums, setIsLoadingDownloadButton);
  };

  useEffect(() => {
    const addScrollListener = (event) => {
      const scrollArea = event.target;
      const delta =
        scrollArea.scrollHeight -
        scrollArea.scrollTop -
        scrollArea.getBoundingClientRect().height;

      if (Math.floor(delta) <= 0) {
        fetchNextPage();
      }
    };

    const container = document.querySelector(
      '[data-testid="photo-album-grid"]'
    );

    if (hasData && container) {
      container.addEventListener('scroll', addScrollListener);
    }

    return () => {
      if (container) {
        container.removeEventListener('scroll', addScrollListener);
      }
    };
  }, [hasData]);

  return (
    <Container data-testid="photo-album-container">
      <FilterBuilderContainer>
        <FilterBuilder
          defaultSelected={['dateStart', 'dateEnd', 'driverIDs']}
          onSubmit={handleFilterSubmit}
          fields={filterBuilderFields}
          dataTestId="filter-builder-photoalbum"
          ref={filterBuilderRef}
        />
        <div
          className="monetInlineAlerContainer"
          data-testid="photoalbum-filter-builder-error"
        />
      </FilterBuilderContainer>
      <GridContainer flexDirection="column" shouldShow={!hasNoData}>
        <PhotoAlbumSelectionBar
          onChangeSelectAll={handleChangeSelectAll}
          onClickDownload={() => handleClickDownloadPhotos()}
          albums={selectedAlbums}
          isAllChecked={isSelectedAll}
          isLoadingDownloadButton={isLoadingDownloadButton}
          showDownloadButton={Object.values(selectedAlbums).filter(Boolean).length > 0}
        />
        <GridContent
          data-testid="photo-album-grid"
          shouldShow={hasData}
          alignItems="center"
          gap="20"
          templateColumns="repeat(auto-fill, minmax(var(--card-size), 1fr))"
          templateRows="repeat(auto-fit, var(--card-size))"
        >
          {pages &&
            pages.length > 0 &&
            pages.map((group) => (
              <React.Fragment key={uuid()}>
                {group.map((album) => (
                  <PhotoAlbumItem key={album.id} {...album} />
                ))}
              </React.Fragment>
            ))}
          <FlexContainerLoading
            justifyContent="center"
            alignItems="center"
            shouldShow={isLoadingMoreAlbums}
          >
            <Loading message={getResourceTo('loading.message')} isVisible />
          </FlexContainerLoading>
        </GridContent>
        <FlexContainerLoading
          justifyContent="center"
          alignItems="center"
          style={{ flex: 1 }}
          shouldShow={isLoading}
        >
          <Loading message={getResourceTo('loading.message')} isVisible />
        </FlexContainerLoading>
      </GridContainer>
      <FlexContainer
        shouldShow={hasNoData}
        justifyContent="center"
        alignItems="center"
        className="noData"
      >
        <EmptyState
          type="data"
          message={getResourceTo('PhotoAlbum.filter.dataNotFound')}
        />
      </FlexContainer>
      <SelectionLoadingOverlay />
    </Container>
  );
}
